import React, { useEffect, useState } from "react";
import { CalendarClock, Loader2, X } from "lucide-react";
import { toast } from "react-hot-toast";
import useGigs from "../../Sevices/useGigs";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";

const INITIAL_FORM = {
  teamId: "",
  fromDate: "",
  toDate: "",
  maximumDrivers: "",
  category: "",
};

export default function CreateSlotModal({ isOpen, onClose, onCreated }) {
  const { createSlotApi } = useGigs();

  const { teams = [], teamsLoading, fetchDriverTeams } = useDriverTeams();

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    fetchDriverTeams();
  }, [isOpen, fetchDriverTeams]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
  };

  const handleClose = () => {
    if (loading) return;

    resetForm();
    onClose?.();
  };

  const handleCreate = async () => {
    const requiredFields = [
      form.teamId,
      form.fromDate,
      form.toDate,
      form.maximumDrivers,
      form.category,
    ];

    const isFormValid = requiredFields.every(
      (value) => String(value || "").trim() !== "",
    );

    if (!isFormValid) {
      toast.error("All fields are required.");
      return;
    }

    const fromDateTime = new Date(form.fromDate);
    const toDateTime = new Date(form.toDate);

    if (
      Number.isNaN(fromDateTime.getTime()) ||
      Number.isNaN(toDateTime.getTime())
    ) {
      toast.error("Please select valid date and time.");
      return;
    }

    if (toDateTime <= fromDateTime) {
      toast.error("To date and time must be after from date and time.");
      return;
    }

    const maximumDrivers = Number(form.maximumDrivers);

    if (!Number.isInteger(maximumDrivers) || maximumDrivers <= 0) {
      toast.error("Maximum drivers must be a whole number greater than 0.");
      return;
    }

    const success = await createSlotApi(form, setLoading, async () => {
      await onCreated?.();
    });

    if (success) {
      resetForm();
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-5">
      <div className="relative max-h-[95vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
              <CalendarClock size={20} />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Create Driver Slot
              </h2>

              <p className="text-xs text-gray-500">
                Select team, slot duration and driver capacity.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
          <div className="sm:col-span-2">
            <label
              htmlFor="teamId"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Driver Team
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <button
                type="button"
                disabled={teamsLoading || loading}
                onClick={() => setTeamDropdownOpen((prev) => !prev)}
                className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-800 flex items-center justify-between"
              >
                <span>
                  {form.teamId
                    ? teams.find((t) => String(t.id) === form.teamId)
                        ?.team_name ||
                      teams.find((t) => String(t.id) === form.teamId)?.teamName
                    : teamsLoading
                      ? "Loading teams..."
                      : "Select Driver Team"}
                </span>

                <svg
                  className={`w-4 h-4 transition-transform ${
                    teamDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {teamDropdownOpen && (
                <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border border-gray-300 bg-white shadow-lg">
                  {/* Search Box */}
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <input
                      type="text"
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                      placeholder="Search team..."
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </div>

                  {/* Team List */}
                  <div className="max-h-32 overflow-y-auto">
                    {teams
                      .filter((team) => {
                        const search = teamSearch.toLowerCase();

                        return (
                          (team.team_name || team.teamName || "")
                            .toLowerCase()
                            .includes(search) ||
                          String(team.id).toLowerCase().includes(search)
                        );
                      })
                      .map((team) => (
                        <div
                          key={team.id}
                          onClick={() => {
                            setForm((prev) => ({
                              ...prev,
                              teamId: String(team.id),
                            }));

                            setTeamSearch("");
                            setTeamDropdownOpen(false);
                          }}
                          className="cursor-pointer border-b border-gray-100 px-3 py-2 text-sm hover:bg-orange-100 last:border-b-0"
                        >
                          {team.team_name || team.teamName || `Team ${team.id}`}
                        </div>
                      ))}

                    {teams.filter((team) => {
                      const search = teamSearch.toLowerCase();
                      return (
                        (team.team_name || team.teamName || "")
                          .toLowerCase()
                          .includes(search) ||
                        String(team.id).toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No team found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="fromDate"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              From Date & Time
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="fromDate"
              type="datetime-local"
              name="fromDate"
              value={form.fromDate}
              onChange={handleChange}
              disabled={loading}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          </div>

          <div>
            <label
              htmlFor="toDate"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              To Date & Time
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="toDate"
              type="datetime-local"
              name="toDate"
              value={form.toDate}
              min={form.fromDate || undefined}
              onChange={handleChange}
              disabled={loading}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          </div>

          <div>
            <label
              htmlFor="maximumDrivers"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Maximum Drivers
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="maximumDrivers"
              type="number"
              name="maximumDrivers"
              value={form.maximumDrivers}
              min="1"
              step="1"
              placeholder="Enter maximum drivers"
              onChange={handleChange}
              disabled={loading}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          </div>

          <div>
            <label
              htmlFor="category"
              className="mb-1.5 block text-sm font-medium text-gray-700"
            >
              Category
              <span className="ml-1 text-red-500">*</span>
            </label>

            <select
              id="category"
              name="category"
              value={form.category}
              onChange={handleChange}
              disabled={loading}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">Select Category</option>
              <option value="Morning">Morning</option>
              <option value="Breakfast">Breakfast</option>
              <option value="Lunch">Lunch</option>
              <option value="Evening">Evening</option>
              <option value="Dinner">Dinner</option>
              <option value="Night">Night</option>
              <option value="Late Night">Late Night</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="h-10 rounded-xl border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleCreate}
            disabled={loading || teamsLoading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}

            {loading ? "Creating..." : "Create Driver Slot"}
          </button>
        </div>
      </div>
    </div>
  );
}
